import React, { useState } from 'react';
import { HomeIcon } from 'lucide-react';
import { HelmetProvider } from 'react-helmet-async';
import { useParams, useSearchParams } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { LocationModal } from '../components/modals/LocationModal';
import { AddToCart } from '../components/product/AddToCart';
import { BundleDeals } from '../components/product/BundleDeals';
import { ComboComponents } from '../components/product/ComboComponents';
import { PharmacistConsultation } from '../components/product/PharmacistConsultation';
import { PrescriptionRequired } from '../components/product/PrescriptionRequired';
import { ProductImageGallery } from '../components/product/ProductImageGallery';
import { ProductInfo } from '../components/product/ProductInfo';
import { ProductTabs } from '../components/product/ProductTabs';
import { StickyBottomActions } from '../components/product/StickyBottomActions';
import { SEO } from '../components/shared/SEO';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PRODUCT_IMAGES } from '../data/productImages';
type LocationValue = {
  name: string;
} | null;
export function ProductDetailPage() {
  const {
    productId
  } = useParams();
  const [searchParams] = useSearchParams();
  const isFromNow = searchParams.get('from') === 'now';
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationValue>(null);
  const product = {
    id: productId || '1',
    name: 'COSRX Advanced Snail 96 Mucin Power Essence 100ml',
    brand: 'COSRX',
    sku: '135556',
    price: 99.25,
    originalPrice: 106,
    discount: 15,
    rating: 4.8,
    reviewCount: 324,
    inStock: true,
    stockStatus: 'In Stock',
    isGlobal: true,
    images: [PRODUCT_IMAGES[0], PRODUCT_IMAGES[1], PRODUCT_IMAGES[2], PRODUCT_IMAGES[3], PRODUCT_IMAGES[4], PRODUCT_IMAGES[0]],
    tags: ['Dry Skin', 'Dark Circles'],
    prescriptionNeeded: false,
    coldChain: true,
    nowEligible: true,
    temperatureRange: '2–8°C',
    description: 'COSRX Advanced Snail 96 Mucin Power Essence is a lightweight essence that nourishes, repairs, and revitalizes the skin. Formulated with 96.3% Snail Secretion Filtrate, it helps improve skin elasticity, soothe redness, and provide deep hydration for a healthy, glowing complexion.',
    loyaltyPoints: 99
  };
  const productUrlKey = product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const breadcrumbItems = [{
    label: 'Home',
    href: '/'
  }, {
    label: 'Skincare',
    href: '/category/skin-care'
  }, {
    label: 'Essences & Serums',
    href: '/category/essences-serums'
  }, {
    label: product.name
  }];
  return <HelmetProvider data-id="element-7734" data-mp="r8s7dzhtri7s">
      <SEO title={`${product.brand} ${product.name} Price ${product.price} SAR`} description={`Buy ${product.name} by ${product.brand} at Dawaak — authentic product with fast delivery across Saudi Arabia.`} canonical={`/en/${productUrlKey}/`} ogType="product" ogImage={product.images[0]} ogPrice={product.price} productPrice={product.price} productCurrency="SAR" robots="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" twitterCard="summary_large_image" hreflangAr={`/${productUrlKey}/`} hreflangEn={`/en/${productUrlKey}/`} hreflangDefault={`/${productUrlKey}/`} author={product.brand} keywords={[product.brand, product.name, ...product.tags, 'Saudi Arabia', 'online pharmacy'].join(', ')} data-id="element-7735" data-mp="r91p6zspuj2e" />
      <div className="flex min-h-screen flex-col bg-white" data-id="element-7736" data-mp="3gxaof8sd78lc">
        <div className="hidden md:block" data-id="element-7737" data-mp="3gvr9pmmcx5lo">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement data-id="element-7738" data-mp="18nujnchjafv3" />
        </div>
        <main className="flex-grow pb-32 pt-14 md:pt-[var(--header-height-desktop)] lg:pb-0" data-id="element-7739" data-mp="yqy8t045xhnd">
          <MobileInternalHeader title={product.name} data-id="element-7740" data-mp="13oq84f75061l" />
          <PageHeroBreadcrumb breadcrumbs={breadcrumbItems} title={product.name} description={product.description} showTitle={false} showDescription={false} data-id="element-7741" data-mp="13n6vqzolqf53" />

          <div className="mx-auto w-full max-w-[1600px] px-4 pb-8 pt-4 sm:px-6 sm:py-6 lg:px-8 lg:pb-6" data-id="element-7742" data-mp="1wcvxxgd1sl8a">
              <div className="space-y-4 md:hidden" data-id="element-7743" data-mp="1ide4gnot4jsl">
                <ProductImageGallery images={product.images} productName={product.name} discount={product.discount} prescriptionNeeded={product.prescriptionNeeded} data-id="element-7744" data-mp="1mfrazebdolqf" />
                <ProductInfo product={product} data-id="element-7745" data-mp="6xpgpu3qbgqq" />
                <BundleDeals product={product} data-id="element-7746" data-mp="b13ximzhs158" />
                <ComboComponents data-id="element-7747" data-mp="b1x8y19ebetp" />
                <PrescriptionRequired data-id="element-7748" data-mp="3gtb4jqet5vt" />
                <PharmacistConsultation data-id="element-7749" data-mp="3fry49pu29bg" />
              </div>
              <div className="hidden grid-cols-2 gap-6 md:grid lg:hidden" data-id="element-7750" data-mp="3unmlad36kfch">
                <div className="sticky top-36 h-fit space-y-6" data-id="element-7751" data-mp="3upqo0rpcf8b6"><ProductImageGallery images={product.images} productName={product.name} discount={product.discount} prescriptionNeeded={product.prescriptionNeeded} data-id="element-7752" data-mp="dfirg82nqi9y" /><BundleDeals product={product} data-id="element-7753" data-mp="8e3buvj913yy" /></div>
                <div className="space-y-6" data-id="element-7754" data-mp="3gx102hq97tbf"><ProductInfo product={product} data-id="element-7755" data-mp="3gw95994hdzm7" /><AddToCart product={product} data-id="element-7756" data-mp="3bwwkqv1314ms" /><PrescriptionRequired data-id="element-7757" data-mp="3bxo4n8lbhdx8" /><PharmacistConsultation data-id="element-7758" data-mp="3by7pax2cqgey" /></div>
              </div>
              <div className="hidden gap-4 lg:grid lg:grid-cols-[400px_1fr_400px] xl:grid-cols-[420px_1fr_420px] xl:gap-6 2xl:grid-cols-[440px_1fr_440px]" data-id="element-7759" data-mp="3qmavo7kb3u4a">
                <div className="sticky top-24 h-fit" data-id="element-7760" data-mp="3lszlpxk76x85"><ProductImageGallery images={product.images} productName={product.name} discount={product.discount} prescriptionNeeded={product.prescriptionNeeded} data-id="element-7761" data-mp="2y4yfwweeluwf" /></div>
                <div className="space-y-6" data-id="element-7762" data-mp="2t4kh84yozxb2"><div className="sticky top-24 space-y-3" data-id="element-7763" data-mp="3iv2d5b6xxfzs"><ProductInfo product={product} data-id="element-7764" data-mp="3iu11p5m0irhj" /></div></div>
                <div className="sticky top-24 h-fit space-y-4 xl:space-y-6" data-id="element-7765" data-mp="3mvm8c97ye9ja"><BundleDeals product={product} data-id="element-7766" data-mp="3mu1deihq8okw" /><AddToCart product={product} data-id="element-7767" data-mp="3rn48iv25k637" /><PrescriptionRequired data-id="element-7768" data-mp="3rnvzfskt1fn5" /><PharmacistConsultation data-id="element-7769" data-mp="3rnmfomqcs4pw" /></div>
              </div>
              <div className="mb-6 mt-6 lg:mb-0 lg:mt-8" data-id="element-7770" data-mp="fi0yrcbbjh2"><ProductTabs product={product} data-id="element-7771" data-mp="inpo5h14jc9" /></div>
            </div>
        </main>
        <div className="hidden md:block" data-id="element-7772" data-mp="2zx9bec81zc3z"><Footer data-id="element-7773" data-mp="33x9zypov93or" /></div>
        <MobileFooterNav data-id="element-7774" data-mp="33wqi18z7ufaq" />
        <PharmacistChat data-id="element-7775" data-mp="33y1jv393pcz7" />
        <StickyBottomActions product={product} isNow={isFromNow} data-id="element-7776" data-mp="33zcnn32jqkqb" />
        <button type="button" aria-label="Back to top" className="fixed bottom-44 right-4 z-40 rounded-full bg-brand p-3 text-white shadow-lg transition-colors duration-150 hover:bg-brand-dark md:bottom-6 md:right-6" onClick={() => window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })} data-id="element-7777" data-mp="38vbldh9rlf3e">
          <HomeIcon className="h-5 w-5" aria-hidden="true" data-id="element-7778" data-mp="t4fkexs9fmbe" />
        </button>
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-7779" data-mp="2dmztkdysvjis" />
      </div>
    </HelmetProvider>;
}